import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';
import './ItemConfigModal.css';

function ItemConfigModal({ item, onAdd, onClose }) {
  const { t } = useTranslation();
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');

  // Escape key handler — closes only this modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isValid = useMemo(() => {
    if (!item.modifierGroups) return true;
    return item.modifierGroups.every((group) => {
      if (!group.required) return true;
      const selected = selectedModifiers[group.groupId] || [];
      return selected.length >= (group.minSelect || 1);
    });
  }, [item.modifierGroups, selectedModifiers]);

  const modifiersPrice = useMemo(() => {
    let total = 0;
    Object.values(selectedModifiers).forEach((options) => {
      options.forEach((opt) => {
        total += opt.price || 0;
      });
    });
    return total;
  }, [selectedModifiers]);

  const unitPrice = item.price + modifiersPrice;
  const totalPrice = unitPrice * quantity;

  const handleModifierChange = (group, option) => {
    setSelectedModifiers((prev) => {
      const current = prev[group.groupId] || [];
      const maxSelect = group.maxSelect || 1;

      if (maxSelect === 1) {
        // Radio: replace selection
        const isSelected = current.length === 1 && current[0].optionId === option.id;
        if (isSelected && !group.required) {
          // Deselect if not required
          const { [group.groupId]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [group.groupId]: [{
            groupId: group.groupId,
            groupName: group.name,
            optionId: option.id,
            optionName: option.name,
            price: option.price || 0,
          }],
        };
      }

      // Checkbox: toggle
      const exists = current.find((o) => o.optionId === option.id);
      if (exists) {
        const filtered = current.filter((o) => o.optionId !== option.id);
        return { ...prev, [group.groupId]: filtered };
      }
      if (current.length >= maxSelect) {
        // Remove oldest, add new
        const trimmed = current.slice(1);
        return {
          ...prev,
          [group.groupId]: [...trimmed, {
            groupId: group.groupId,
            groupName: group.name,
            optionId: option.id,
            optionName: option.name,
            price: option.price || 0,
          }],
        };
      }
      return {
        ...prev,
        [group.groupId]: [...current, {
          groupId: group.groupId,
          groupName: group.name,
          optionId: option.id,
          optionName: option.name,
          price: option.price || 0,
        }],
      };
    });
  };

  const handleAdd = () => {
    const flatModifiers = Object.values(selectedModifiers).flat();
    onAdd({
      ...item,
      quantity,
      notes: notes.trim() || undefined,
      selectedModifiers: flatModifiers,
      unitPrice: item.price,
      price: unitPrice,
    });
  };

  const isOptionSelected = (groupId, optionId) => {
    const group = selectedModifiers[groupId] || [];
    return group.some((o) => o.optionId === optionId);
  };

  return (
    <div className="item-config-overlay" onClick={onClose}>
      <div className="item-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="item-config-header">
          <h3 className="item-config-title">{item.name}</h3>
          <span className="item-config-base-price">
            {formatCurrency(item.price)}
          </span>
        </div>

        {item.description && (
          <p className="item-config-description">{item.description}</p>
        )}

        <div className="item-config-body">
          {item.modifierGroups?.map((group) => (
            <div key={group.groupId} className="item-config-modifier-group">
              <div className="item-config-group-header">
                <span className="item-config-group-name">{group.name}</span>
                <span className={`item-config-group-tag ${group.required ? 'tag-required' : 'tag-optional'}`}>
                  {group.required ? t('waiterOrder.required') : t('waiterOrder.optional')}
                </span>
              </div>
              <div className="item-config-options">
                {group.options?.map((option) => {
                  const selected = isOptionSelected(group.groupId, option.id);
                  const isRadio = (group.maxSelect || 1) === 1;
                  return (
                    <label
                      key={option.id}
                      className={`item-config-option ${selected ? 'option-selected' : ''} ${option.available === false ? 'option-unavailable' : ''}`}
                    >
                      <input
                        type={isRadio ? 'radio' : 'checkbox'}
                        name={`modifier-${group.groupId}`}
                        checked={selected}
                        onChange={() => handleModifierChange(group, option)}
                        disabled={option.available === false}
                      />
                      <span className="option-name">{option.name}</span>
                      {option.price > 0 && (
                        <span className="option-price">
                          +{formatCurrency(option.price)}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="item-config-notes">
            <label className="item-config-notes-label">{t('waiterOrder.notes')}</label>
            <textarea
              className="item-config-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('waiterOrder.notesPlaceholder')}
              rows={2}
            />
          </div>
        </div>

        <div className="item-config-footer">
          <div className="item-config-qty">
            <button
              className="item-config-qty-btn"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
            >
              −
            </button>
            <span className="item-config-qty-value">{quantity}</span>
            <button
              className="item-config-qty-btn"
              onClick={() => setQuantity((q) => q + 1)}
            >
              +
            </button>
          </div>
          <button
            className="item-config-add-btn"
            onClick={handleAdd}
            disabled={!isValid}
          >
            {t('waiterOrder.add')} · {formatCurrency(totalPrice)}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ItemConfigModal;
